import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { exportAzDashboard, fetchAzDashboard } from '../../services/apiService';
import KpiPager from '../../components/KpiPager';
import '../../styles/kpi-theme.css';

interface KpiDashboardProps {
    setError: Dispatch<SetStateAction<string | null>>;
    setIsError: Dispatch<SetStateAction<boolean>>;
}

interface SurveyItem {
    id: number;
    question: string | null;
    response: string | null;
}

interface Session {
    id: string;
    clinic: string | null;
    clinic_id: number | null;
    question: string | null;
    response: string | null;
    profile: string | null;
    // 1 = downloaded the profile, 0 = did not, null = never recorded.
    download: number | null;
    // null when the player never finished the game.
    result: 'won' | 'lost' | null;
    duration: string | null;
    // "YYYY-MM-DD HH:MM" of the session's first event.
    started_at: string | null;
    preGame: SurveyItem[];
    postGame: SurveyItem[];
}

interface Kpis {
    total_sessions: number;
    total_clinics: number;
    completed_count: number;
    completed_percent: number;
    average_duration: number | null;
    top_profile: { id: number; name: string | null; count: number } | null;
}

interface DashboardData {
    project: { name: string | null; code: string | null };
    kpis: Kpis;
    sessions: Session[];
    total_pages: number;
    total_count: number;
    current_page: number;
    per_page: number;
}

type ResultFilter = 'all' | 'won' | 'lost';

const filters: { key: ResultFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'won', label: 'Won' },
    { key: 'lost', label: 'Lost' }
];

const buildKpiCards = (kpis: Kpis | undefined) => [
    {
        label: 'Total Sessions',
        value: kpis ? kpis.total_sessions.toLocaleString() : '—',
        hint: kpis ? `Across ${kpis.total_clinics} ${kpis.total_clinics === 1 ? 'clinic' : 'clinics'}` : 'Across all clinics'
    },
    {
        label: 'Completed Games',
        value: kpis ? `${kpis.completed_percent}%` : '—',
        hint: kpis ? `${kpis.completed_count} of ${kpis.total_sessions} sessions completed` : 'Sessions completed'
    },
    {
        label: 'Average Duration',
        value: kpis?.average_duration != null ? `${kpis.average_duration}s` : '—',
        hint: 'Time spent in experience'
    },
    {
        label: 'Most Common Profile',
        value: kpis?.top_profile ? `P${kpis.top_profile.id}` : '—',
        hint: kpis?.top_profile?.name || 'No profile recorded yet'
    }
];

// The API sends "YYYY-MM-DD HH:MM"; the table shows the date part only.
const toDateOnly = (value: string | null): string => value?.split(' ')[0] || '—';

const SurveyTable: React.FC<{ items: SurveyItem[]; emptyMessage: string }> = ({ items, emptyMessage }) => {
    if (!items.length) {
        return <div className="kpi-empty-post">{emptyMessage}</div>;
    }

    return (
        <table className="kpi-mini-table">
            <thead>
                <tr>
                    <th>ID</th>
                    <th>Question</th>
                    <th>Response</th>
                </tr>
            </thead>
            <tbody>
                {items.map((item) => (
                    <tr key={item.id}>
                        <td>{(emptyMessage === "No post-game survey was recorded for this session." && item.question?.includes("Do you know that high blood sugar can damage organs such as the heart and kidneys?")) ? '6' :item.id}</td>
                        <td>{item.question || '—'}</td>
                        <td><span className="kpi-response-chip">{item.response || '—'}</span></td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

const KpiDashboard: React.FC<KpiDashboardProps> = ({ setError, setIsError }) => {
    const [data, setData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [activeFilter, setActiveFilter] = useState<ResultFilter>('all');
    const [page, setPage] = useState(1);
    const [openRows, setOpenRows] = useState<string[]>([]);
    const [closedPanels, setClosedPanels] = useState<string[]>([]);
    const [exporting, setExporting] = useState(false);

    // Debounce the search box so we don't fire a request per keystroke.
    useEffect(() => {
        const timer = setTimeout(() => {
            setAppliedSearch(search.trim());
            setPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [search]);

    const loadDashboard = useCallback(async () => {
        setLoading(true);
        try {
            const result = await fetchAzDashboard(page, appliedSearch, activeFilter);
            setData(result);
            setOpenRows([]);
        } catch (err) {
            setData(null);
            setIsError(true);
            setError(err instanceof Error ? err.message : 'Unable to load the KPI dashboard');
        } finally {
            setLoading(false);
        }
    }, [page, appliedSearch, activeFilter, setError, setIsError]);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    // Exports every session matching the current search + filter, not just this page.
    const handleExport = async () => {
        setExporting(true);
        try {
            const blob = await exportAzDashboard(appliedSearch, activeFilter);

            // A failed export still arrives as a blob - unwrap the JSON message.
            if (blob.type.includes('json')) {
                const message = JSON.parse(await blob.text())?.message;
                throw new Error(message || 'Export failed');
            }

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${data?.project?.code || 'AZ'}-kpi-dashboard.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setIsError(true);
            setError(err instanceof Error ? err.message : 'Unable to export the dashboard data');
        } finally {
            setExporting(false);
        }
    };

    const changeFilter = (filter: ResultFilter) => {
        setActiveFilter(filter);
        setPage(1);
    };

    const toggleRow = (id: string) => {
        setOpenRows((prev) => (prev.includes(id) ? prev.filter((row) => row !== id) : [...prev, id]));
    };

    const togglePanel = (panelId: string) => {
        setClosedPanels((prev) => (prev.includes(panelId) ? prev.filter((panel) => panel !== panelId) : [...prev, panelId]));
    };

    const sessions = data?.sessions ?? [];
    const totalCount = data?.total_count ?? 0;
    const totalPages = data?.total_pages ?? 1;
    const perPage = data?.per_page ?? 20;
    const firstRow = totalCount === 0 ? 0 : (page - 1) * perPage + 1;
    const lastRow = Math.min(page * perPage, totalCount);

    return (
        <div className="kpi-scope">
            <main className="kpi-page">
                <section className="kpi-header">
                    <div className="kpi-title-wrap">
                        <h1>AstraZeneca KPI Dashboard</h1>
                    </div>
                    <div className="kpi-header-actions">
                        <button
                            type="button"
                            className="kpi-export-btn"
                            onClick={handleExport}
                            disabled={exporting || loading}
                        >
                            {exporting ? 'Preparing...' : 'Export Data'}
                        </button>
                        <div className="kpi-project-pill">
                            <strong>{data?.project?.name || 'Sugar Attack: Back To Target'}</strong>
                        </div>
                    </div>
                </section>

                <section className="kpis">
                    {buildKpiCards(data?.kpis).map((card) => (
                        <div className="kpi-card" key={card.label}>
                            <label>{card.label}</label>
                            <div className="value">{card.value}</div>
                            <div className="hint">{card.hint}</div>
                        </div>
                    ))}
                </section>

                <section className="kpi-toolbar">
                    <input
                        className="kpi-search"
                        type="search"
                        placeholder="Search by session, clinic, question, response, or profile..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <div className="kpi-filter-group">
                        {filters.map((filter) => (
                            <button
                                key={filter.key}
                                type="button"
                                className={`kpi-filter ${activeFilter === filter.key ? 'active' : ''}`}
                                onClick={() => changeFilter(filter.key)}
                            >
                                {filter.label}
                            </button>
                        ))}
                    </div>
                </section>

                <section className="kpi-dashboard-card">
                    <table>
                        <thead>
                            <tr>
                                <th>Session ID</th>
                                <th>Clinic Name</th>
                                <th>Question</th>
                                <th>Response</th>
                                <th>Profile Type</th>
                                <th>Downloaded the Profile</th>
                                <th>Game Result</th>
                                <th>Duration</th>
                                <th>Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={9}><div className="kpi-state">Loading sessions...</div></td>
                                </tr>
                            )}
                            {!loading && sessions.length === 0 && (
                                <tr>
                                    <td colSpan={9}>
                                        <div className="kpi-state">
                                            {appliedSearch || activeFilter !== 'all'
                                                ? 'No sessions match your search.'
                                                : 'No sessions recorded yet.'}
                                        </div>
                                    </td>
                                </tr>
                            )}
                            {!loading && sessions.map((session) => {
                                const isOpen = openRows.includes(session.id);
                                const preId = `pre-${session.id}`;
                                const postId = `post-${session.id}`;

                                return (
                                    <React.Fragment key={`${session.clinic_id}-${session.id}`}>
                                        <tr
                                            className={`kpi-session-row ${isOpen ? 'open' : ''}`}
                                            onClick={() => toggleRow(session.id)}
                                        >
                                            <td className="kpi-session-id">
                                                <span className="kpi-expand-icon">›</span>{session.id}
                                            </td>
                                            <td>{session.clinic || '—'}</td>
                                            <td>{session.question || '—'}</td>
                                            <td><span className="kpi-response-chip">{session.response || '—'}</span></td>
                                            <td><div className="kpi-profile-summary">{session.profile || '—'}</div></td>
                                            <td>
                                                {session.download === null || session.download === undefined ? '—' : (
                                                    <span className={`kpi-badge ${session.download === 1 ? 'green' : 'red'}`}>
                                                        {session.download === 1 ? 'YES' : 'NO'}
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                {session.result ? (
                                                    <span className={`kpi-badge ${session.result === 'won' ? 'green' : 'red'}`}>
                                                        {session.result.toUpperCase()}
                                                    </span>
                                                ) : (
                                                    <span className="kpi-badge purple">INCOMPLETE</span>
                                                )}
                                            </td>
                                            <td>
                                                {session.duration
                                                    ? <span className="kpi-badge blue">{session.duration}</span>
                                                    : '—'}
                                            </td>
                                            <td className="kpi-datetime">{toDateOnly(session.started_at)}</td>
                                        </tr>
                                        {isOpen && (
                                            <tr className="kpi-details-row">
                                                <td colSpan={9}>
                                                    <div className="kpi-details-wrap">
                                                        <div className="kpi-survey-panel">
                                                            <div className="kpi-panel-title" onClick={() => togglePanel(preId)}>
                                                                Pre-game Survey
                                                            </div>
                                                            {!closedPanels.includes(preId) && (
                                                                <div className="kpi-survey-content">
                                                                    <SurveyTable
                                                                        items={session.preGame}
                                                                        emptyMessage="No pre-game survey was recorded for this session."
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="kpi-survey-panel">
                                                            <div className="kpi-panel-title" onClick={() => togglePanel(postId)}>
                                                                Post-game Survey
                                                            </div>
                                                            {!closedPanels.includes(postId) && (
                                                                <div className="kpi-survey-content">
                                                                    <SurveyTable
                                                                        items={session.postGame}
                                                                        emptyMessage="No post-game survey was recorded for this session."
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>

                    <div className="kpi-table-meta">
                        <div>
                            {totalCount === 0
                                ? 'Showing 0 sessions'
                                : `Showing ${firstRow}–${lastRow} of ${totalCount.toLocaleString()} sessions`}
                        </div>
                        <KpiPager page={page} totalPages={totalPages} disabled={loading} onChange={setPage} />
                    </div>
                </section>
            </main>
        </div>
    );
};

export default KpiDashboard;
